import { CameraOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { StatusRadioGroup, UnsavedChangesModal } from '@components';
import { useDocumentSasUrl, useInspection } from '@hooks';
import { CreateInspectionResponses } from '@services';
import { useStore } from '@stores';
import { Utils } from '@utils';
import {
  Button,
  Col,
  Collapse,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Row,
  Typography,
  Upload,
} from 'antd';
import { RcFile } from 'antd/es/upload';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';

const { TextArea } = Input;

const DocumentsField: React.FC<{ value?: any[]; onChange?: (val: any[]) => void }> = () => null;

interface IProps {
  open: boolean;
  inspectionId: string | undefined;
  onCancel: () => void;
}

export const InspectionFormModal: React.FC<IProps> = observer(
  ({ open, inspectionId, onCancel }) => {
    const [form] = Form.useForm();
    const { inspectionStore } = useStore();
    const { submitResponsesLoading } = inspectionStore;
    const modalBodyRef = React.useRef<HTMLDivElement>(null);
    const [openCancelModal, setOpenCancelModal] = React.useState(false);

    const { loading, inspection } = useInspection(inspectionId);

    const queryString = useDocumentSasUrl();
    const afterQuestionMark = queryString.split('?')[1];

    useEffect(() => {
      if (open && inspection && afterQuestionMark) {
        form.setFieldsValue({
          responses: inspection.category.subCategories.flatMap((sub) =>
            sub.items.map((item) => {
              const match = inspection.responses?.find((r) => r.subCategoryItemId === item.id);
              return {
                subCategoryItemId: item.id,
                subCategoryName: item.name,
                subCategoryParent: sub.name,
                id: match?.id || undefined,
                status: match?.status || null,
                depth: match?.depth || null,
                note: match?.note || '',
                inspectionId: inspection.id,
                documents:
                  match?.documents.map((doc) => ({
                    ...doc,
                    url: `${doc.url}?${afterQuestionMark}`,
                  })) || [],
              };
            })
          ),
        });
      }
    }, [open, inspection, form, afterQuestionMark]);

    const handleFinish = React.useCallback(
      (values: CreateInspectionResponses) => {
        const filteredResponses = {
          ...values,
          responses: values.responses.map((resp) => ({
            ...resp,
            documents: (resp.documents ?? []).filter((doc) => !doc.id),
          })),
        };

        inspectionId &&
          inspectionStore
            .createResponses({ id: inspectionId, responses: filteredResponses.responses })
            .then(onCancel);
      },
      [inspectionId, inspectionStore, onCancel]
    );

    const handleCancel = () => {
      setOpenCancelModal(false);
      onCancel();
    };

    return (
      <>
        <Modal
          title={!loading && `Inspection - ${inspection?.category.name || ''}`}
          open={open}
          onCancel={() => setOpenCancelModal(true)}
          destroyOnHidden
          loading={loading}
          confirmLoading={loading}
          footer={
            <Row justify="end" style={{ marginTop: 16 }}>
              <Col>
                <Button
                  onClick={() => setOpenCancelModal(true)}
                  style={{ marginRight: 8 }}
                  disabled={submitResponsesLoading}
                >
                  Leave
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  onClick={() => form.submit()}
                  loading={submitResponsesLoading}
                  disabled={submitResponsesLoading}
                >
                  Save
                </Button>
              </Col>
            </Row>
          }
        >
          <div ref={modalBodyRef} style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <Form form={form} layout="vertical" onFinish={handleFinish}>
              <Form.List name="responses">
                {(fields) => {
                  const groupedBySubCat = fields.reduce<Record<string, typeof fields>>(
                    (acc, field) => {
                      const subCatParent = form.getFieldValue([
                        'responses',
                        field.name,
                        'subCategoryParent',
                      ]);
                      if (!acc[subCatParent]) acc[subCatParent] = [];
                      acc[subCatParent].push(field);
                      return acc;
                    },
                    {}
                  );
                  const sortedSubCategories = (inspection?.category.subCategories || []).sort(
                    (a, b) => (a.orderPosition ?? 0) - (b.orderPosition ?? 0)
                  );

                  const collapseItems = sortedSubCategories.map((sub) => {
                    const subCatParent = sub.name;
                    const subCatFields = groupedBySubCat[subCatParent] || [];
                    return {
                      key: subCatParent,
                      label: subCatParent,
                      children: (
                        <>
                          {subCatFields.map(({ key, name, ...restField }) => {
                            const subCatName = form.getFieldValue([
                              'responses',
                              name,
                              'subCategoryName',
                            ]);
                            const item = sub.items.find((itm) => itm.name === subCatName);

                            return (
                              <div
                                key={key}
                                style={{
                                  marginBottom: 16,
                                  padding: 12,
                                  borderRadius: 8,
                                  background: '#fafafa',
                                  border: '1px solid #f0f0f0',
                                }}
                              >
                                <Form.Item hidden {...restField} name={[name, 'id']}>
                                  <Input type="hidden" />
                                </Form.Item>

                                <Form.Item hidden {...restField} name={[name, 'inspectionId']}>
                                  <Input type="hidden" />
                                </Form.Item>
                                <Form.Item {...restField} name={[name, 'subCategoryItemId']} hidden>
                                  <Input type="hidden" />
                                </Form.Item>
                                <Form.Item {...restField} name={[name, 'documents']} hidden noStyle>
                                  <DocumentsField />
                                </Form.Item>
                                {/* Sub-category Item Name and Depth */}
                                <Row
                                  gutter={12}
                                  align="middle"
                                  wrap={false}
                                  style={{ marginBottom: 8 }}
                                >
                                  <Col span={item?.showDepth ? 14 : 24}>
                                    <Typography.Text strong style={{ fontSize: 16 }}>
                                      {subCatName}
                                    </Typography.Text>
                                  </Col>

                                  {item?.showDepth && (
                                    <Col span={10}>
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'depth']}
                                        style={{ marginBottom: 0 }}
                                      >
                                        <InputNumber
                                          placeholder="Enter depth"
                                          style={{ width: '100%' }}
                                        />
                                      </Form.Item>
                                    </Col>
                                  )}
                                </Row>
                                {/* Status Radio Group */}
                                <Row justify="center" align="middle" gutter={4}>
                                  <Col>
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'status']}
                                      style={{ marginBottom: 4 }}
                                    >
                                      <StatusRadioGroup />
                                    </Form.Item>
                                  </Col>
                                  <Col>
                                    <Form.Item>
                                      <Button
                                        type="link"
                                        onClick={() =>
                                          form.setFieldValue(['responses', name, 'status'], null)
                                        }
                                      >
                                        Clear
                                      </Button>
                                    </Form.Item>
                                  </Col>
                                </Row>
                                {/* Note and Upload */}
                                <Row gutter={4}>
                                  <Col span={20}>
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'note']}
                                      style={{ marginBottom: 4 }}
                                    >
                                      <TextArea
                                        placeholder="Enter note"
                                        autoSize={{ minRows: 1, maxRows: 3 }}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col span={4}>
                                    <Form.Item style={{ marginBottom: 0 }}>
                                      <Upload
                                        multiple
                                        showUploadList={false}
                                        beforeUpload={(file) => {
                                          Utils.getBase64(file as RcFile, (base64Url) => {
                                            const currentDocs =
                                              form.getFieldValue([
                                                'responses',
                                                name,
                                                'documents',
                                              ]) || [];
                                            if (currentDocs.length > 3) {
                                              return;
                                            }
                                            const newDoc = {
                                              name: file.name,
                                              type: file.type,
                                              content: base64Url.split(',')[1],
                                              contentType: file.type,
                                            };
                                            form.setFieldValue(
                                              ['responses', name, 'documents'],
                                              [...currentDocs, newDoc]
                                            );
                                          });

                                          return false;
                                        }}
                                        accept="image/*"
                                      >
                                        <Button
                                          type="link"
                                          disabled={
                                            (
                                              form.getFieldValue([
                                                'responses',
                                                name,
                                                'documents',
                                              ]) || []
                                            ).length > 3
                                          }
                                          icon={<CameraOutlined style={{ fontSize: 30 }} />}
                                          style={{ width: 48 }}
                                        />
                                      </Upload>
                                    </Form.Item>
                                  </Col>
                                </Row>
                                <Row>
                                  <Col>
                                    <Form.Item
                                      noStyle
                                      shouldUpdate={(prev, cur) =>
                                        prev.responses?.[name]?.documents !==
                                        cur.responses?.[name]?.documents
                                      }
                                    >
                                      {() => {
                                        const docs =
                                          form.getFieldValue(['responses', name, 'documents']) ||
                                          [];

                                        // 🔥 Don't render anything if there are no images
                                        if (!Array.isArray(docs) || docs.length === 0) return null;

                                        return (
                                          <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                                            {docs.slice(0, 4).map((doc: any, index: number) => (
                                              <Col span={6} key={index}>
                                                <div
                                                  style={{
                                                    width: '100%',
                                                    height: 60,
                                                    borderRadius: 8,
                                                    overflow: 'hidden',
                                                    border: '1px solid #ddd',
                                                    position: 'relative',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                    background: '#fafafa',
                                                  }}
                                                >
                                                  <Image
                                                    src={
                                                      doc.id
                                                        ? doc.url
                                                        : `data:${doc.contentType};base64,${doc.content}`
                                                    }
                                                    alt={doc.name}
                                                    style={{
                                                      width: '100%',
                                                      height: '100%',
                                                      objectFit: 'cover',
                                                    }}
                                                    preview={true}
                                                  />

                                                  {!doc.id && (
                                                    <CloseCircleOutlined
                                                      onClick={() => {
                                                        const newFiles = docs.filter(
                                                          (_, i) => i !== index
                                                        );
                                                        form.setFieldValue(
                                                          ['responses', name, 'documents'],
                                                          newFiles
                                                        );
                                                      }}
                                                      style={{
                                                        position: 'absolute',
                                                        top: 4,
                                                        right: 4,
                                                        color: '#fff',
                                                        background: 'rgba(0,0,0,0.55)',
                                                        borderRadius: '50%',
                                                        fontSize: 16,
                                                        cursor: 'pointer',
                                                        padding: 2,
                                                      }}
                                                    />
                                                  )}
                                                </div>
                                              </Col>
                                            ))}
                                          </Row>
                                        );
                                      }}
                                    </Form.Item>
                                  </Col>
                                </Row>
                              </div>
                            );
                          })}
                        </>
                      ),
                    };
                  });

                  return (
                    <Collapse
                      accordion
                      items={collapseItems}
                      onChange={() => {
                        if (modalBodyRef.current) {
                          modalBodyRef.current.scrollTop = 0;
                        }
                      }}
                    />
                  );
                }}
              </Form.List>
            </Form>
          </div>
        </Modal>
        <UnsavedChangesModal
          loading={submitResponsesLoading}
          open={openCancelModal}
          onOk={() => form.submit()}
          onLeave={handleCancel}
          onCancel={() => setOpenCancelModal(false)}
        />
      </>
    );
  }
);
